/**
 * Eyebrow shape charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nails_shades`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eyebrowShapeChartsPack: ToilePack = {
    slug: 'eyebrow-shape-charts',
    category: 'beauty',
    name: 'Eyebrow shape charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'shapes', label: 'Brow shapes', subject: 'a chart of eight eyebrow shapes, "Arched", "Straight", "Soft angled", "Rounded", "Hard angled", "S-shaped", "Flat" and "Upward", each labelled'},
        {key: 'mapping', label: 'Brow mapping', subject: 'a face outline with lines from the nose to the start, arch and tail of the brow'},
        {key: 'match', label: 'Face match', subject: 'a chart pairing face shapes with their best eyebrow shape'},
    ],
    styles: [
        {key: 'line', label: 'Line diagram', style: 'technical line art with uniform thin black outlines, the brow hairs as fine short strokes and any guide as a dashed line', palette: 'black ink only', framing: 'centered on the page with a wide white margin'},
        {key: 'tint', label: 'Soft brown', style: 'a clean outline drawing with the brows filled in soft feathered strokes', palette: 'brows in taupe and dark brown, guide lines in muted rose, everything else left white', framing: 'centered on the page with a wide white margin'},
        {key: 'pencil', label: 'Pencil study', style: 'a fine realistic pencil drawing of each brow with soft graphite tone under the hair strokes', palette: 'graphite gray only', framing: 'drawn large on the page with a white margin all round'},
    ],
    directions: ["men's brows", 'brow lamination', 'thin 90s brows'],
    directionHint: "try: men's brows",
    seo: {
        title: 'Eyebrow Shape Charts: Shapes, Mapping, Face Match',
        metaDescription: 'Eyebrow shape charts: eight named brow shapes, a mapping guide and a face-shape match, in line, soft brown or pencil.',
        h1: 'Eyebrow shape charts: shapes, mapping, face match',
        tagline: 'Nine brow pages: the shapes, a mapping guide and a face-shape match, in three drawing styles.',
        intro: 'Eyebrow shape charts help you choose a brow for your face. The shape chart names eight brows: arched, straight, soft angled, rounded, hard angled, S-shaped, flat and upward. A mapping guide draws lines from the nose to the start, arch and tail of the brow, and a face match pairs each face shape with a suited brow. Type a different group or a twist, such as brow lamination or thin 90s brows, and the same drawing style carries it.',
        useCases: [
            'a brow consultation handout',
            'a practice sheet for a makeup or brow course',
            'a reference for a salon brow appointment',
            'a face-shape guide for a styling session',
        ],
        faq: [
            {q: 'What are the eight brow shapes?', a: 'Arched, straight, soft angled, rounded, hard angled, S-shaped, flat and upward.'},
            {q: 'How does the brow mapping work?', a: 'Lines run from the nose to the start, the arch and the tail of the brow, the three points that place it on the face.'},
            {q: 'What does the face match show?', a: 'Face shapes, each paired with a brow shape that suits it.'},
            {q: 'What are the three drawing styles?', a: 'A line diagram with thin black outlines, a soft brown drawing with feathered strokes, and a pencil study with graphite tone under the hair strokes.'},
        ],
    },
}
